// Typed access to the Supabase edge functions.
//
// The Supabase JS client's functions.invoke() swallows the response body on a
// non-2xx status, which loses the machine-readable `error` code every function
// returns. These helpers use fetch directly so the caller can branch on the
// code and show a real message instead of "something went wrong".

import { supabase } from './supabase'
import { env } from './env'

const FUNCTIONS_BASE = `${env.VITE_PUBLIC_SUPABASE_URL}/functions/v1`

export type FnResult<T> = { ok: true; data: T } | { ok: false; code: string; status: number }

async function authHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return {
    Authorization: `Bearer ${session?.access_token ?? ''}`,
    apikey: env.VITE_PUBLIC_SUPABASE_ANON_KEY,
  }
}

async function toResult<T>(res: Response): Promise<FnResult<T>> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    body = null
  }
  if (!res.ok) {
    const code =
      body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : 'request_failed'
    return { ok: false, code, status: res.status }
  }
  return { ok: true, data: body as T }
}

export async function callFunction<T>(
  name: string,
  body: unknown,
  init: { method?: 'POST' | 'GET' } = {},
): Promise<FnResult<T>> {
  try {
    const res = await fetch(`${FUNCTIONS_BASE}/${name}`, {
      method: init.method ?? 'POST',
      headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
      ...(init.method === 'GET' ? {} : { body: JSON.stringify(body ?? {}) }),
    })
    return await toResult<T>(res)
  } catch {
    return { ok: false, code: 'network_error', status: 0 }
  }
}

export async function getFunction<T>(
  name: string,
  params: Record<string, string>,
): Promise<FnResult<T>> {
  try {
    const qs = new URLSearchParams(params).toString()
    const res = await fetch(`${FUNCTIONS_BASE}/${name}?${qs}`, {
      method: 'GET',
      headers: await authHeaders(),
    })
    return await toResult<T>(res)
  } catch {
    return { ok: false, code: 'network_error', status: 0 }
  }
}

/** Multipart upload — cannot go through callFunction, which sets a JSON body. */
export async function uploadFunction<T>(name: string, form: FormData): Promise<FnResult<T>> {
  try {
    const res = await fetch(`${FUNCTIONS_BASE}/${name}`, {
      method: 'POST',
      headers: await authHeaders(), // no Content-Type: the browser sets the boundary
      body: form,
    })
    return await toResult<T>(res)
  } catch {
    return { ok: false, code: 'network_error', status: 0 }
  }
}

/** Unauthenticated call — /pricing runs before there is a session. */
export async function callPublicFunction<T>(name: string, body: unknown): Promise<FnResult<T>> {
  try {
    const res = await fetch(`${FUNCTIONS_BASE}/${name}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        Authorization: `Bearer ${env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(body ?? {}),
    })
    return await toResult<T>(res)
  } catch {
    return { ok: false, code: 'network_error', status: 0 }
  }
}
